import { type Graph } from '../models/graph';
import { type BackgroundProjection } from './backgrounds';
import { graphBounds, type Point, type Rect } from './cityPlan';

export interface Viewport {
  readonly scale: number;
  readonly scene: Rect;
  readonly projection: BackgroundProjection;
  project(position: Point): Point;
}

/** Canvas edges the graph keeps clear of, in CSS pixels; the city still fills them. */
export interface Insets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

const MIN_EXTENT = 0.1;
const SCENE_BLEED = 0.02;

export function uniformInsets(inset: number): Insets {
  return { top: inset, right: inset, bottom: inset, left: inset };
}

export function insetKey(insets: Insets): string {
  return `${insets.top},${insets.right},${insets.bottom},${insets.left}`;
}

export function fitViewport(
  graph: Graph | null,
  width: number,
  height: number,
  inset: number | Insets,
): Viewport {
  const insets = typeof inset === 'number' ? uniformInsets(inset) : inset;
  const bounds = graphBounds(graph ?? { nodes: [], edges: [], directed: false });
  const extentX = Math.max(bounds.maxX - bounds.minX, MIN_EXTENT);
  const extentY = Math.max(bounds.maxY - bounds.minY, MIN_EXTENT);

  const usableWidth = width - insets.left - insets.right;
  const usableHeight = height - insets.top - insets.bottom;
  const scale = Math.min(usableWidth / extentX, usableHeight / extentY);
  const offsetX = insets.left + (usableWidth - extentX * scale) / 2;
  const offsetY = insets.top + (usableHeight - extentY * scale) / 2;

  const project = (position: Point): Point => ({
    x: offsetX + (position.x - bounds.minX) * scale,
    y: offsetY + (position.y - bounds.minY) * scale,
  });

  const scene: Rect = {
    minX: bounds.minX - offsetX / scale - SCENE_BLEED,
    minY: bounds.minY - offsetY / scale - SCENE_BLEED,
    maxX: bounds.minX + (width - offsetX) / scale + SCENE_BLEED,
    maxY: bounds.minY + (height - offsetY) / scale + SCENE_BLEED,
  };

  return { scale, scene, project, projection: { toCanvas: project, unitScale: scale } };
}

export interface BoardView extends Viewport {
  readonly board: Rect;
  readonly frame: number;
  readonly reach: number;
}

const BOARD_GUTTER = 0.02;
const BOARD_FRAME = 0.075;
const BOARD_MARGIN = 0.12;
const BOARD_LIFT = 0.6;
const BOARD_SPREAD = 1.2;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// The level lies on a framed tray: the tray and its frame fit into the canvas
// between the HUD insets, and the graph sits inside the tray with a margin of
// board around it for the blocks along its outer streets.
export function fitBoard(
  graph: Graph | null,
  width: number,
  height: number,
  safe: Insets,
): BoardView {
  const bounds = graphBounds(graph ?? { nodes: [], edges: [], directed: false });
  const extentX = Math.max(bounds.maxX - bounds.minX, MIN_EXTENT);
  const extentY = Math.max(bounds.maxY - bounds.minY, MIN_EXTENT);

  const span = Math.min(width - safe.left - safe.right, height - safe.top - safe.bottom);
  const frame = clamp((span - 200) * BOARD_FRAME, 9, 30);
  const gutter = clamp(Math.min(width, height) * BOARD_GUTTER, 6, 16) + frame * 0.5;
  const areaLeft = safe.left + gutter;
  const areaTop = safe.top + gutter;
  const areaWidth = width - safe.right - gutter - areaLeft;
  const areaHeight = height - safe.bottom - gutter - areaTop;
  const innerWidth = areaWidth - frame * 2;
  const innerHeight = areaHeight - frame * 2;
  const margin = clamp((Math.min(innerWidth, innerHeight) - 200) * BOARD_MARGIN, 14, 52);
  const lift = margin * BOARD_LIFT;

  const scale = Math.min(
    (innerWidth - margin * 2) / extentX,
    (innerHeight - margin * 2 - lift) / extentY,
  );
  const graphWidth = extentX * scale;
  const graphHeight = extentY * scale;
  const boardWidth = Math.min(innerWidth, (graphWidth + margin * 2) * (1 + BOARD_SPREAD));
  const boardHeight = Math.min(innerHeight, (graphHeight + margin * 2 + lift) * (1 + BOARD_SPREAD));
  const boardLeft = areaLeft + frame + (innerWidth - boardWidth) / 2;
  const boardTop = areaTop + frame + (innerHeight - boardHeight) / 2;
  const offsetX = boardLeft + (boardWidth - graphWidth) / 2;
  const offsetY = boardTop + lift + (boardHeight - lift - graphHeight) / 2;

  const project = (position: Point): Point => ({
    x: offsetX + (position.x - bounds.minX) * scale,
    y: offsetY + (position.y - bounds.minY) * scale,
  });
  const unproject = (x: number, y: number): Point => ({
    x: bounds.minX + (x - offsetX) / scale,
    y: bounds.minY + (y - offsetY) / scale,
  });
  const topLeft = unproject(boardLeft, boardTop);
  const bottomRight = unproject(boardLeft + boardWidth, boardTop + boardHeight);
  const canvasTopLeft = unproject(0, 0);
  const canvasBottomRight = unproject(width, height);

  return {
    scale,
    scene: {
      minX: canvasTopLeft.x,
      minY: canvasTopLeft.y,
      maxX: canvasBottomRight.x,
      maxY: canvasBottomRight.y,
    },
    board: { minX: topLeft.x, minY: topLeft.y, maxX: bottomRight.x, maxY: bottomRight.y },
    frame: frame / scale,
    reach: span / scale,
    project,
    projection: { toCanvas: project, unitScale: scale },
  };
}
